export interface MemoryObject {
  id: string;
  type: string;
  position: { x: number; y: number };
  color: string;
}

export interface GameStats {
  timestamp: string;
  accuracy: number;
  timeSeconds: number;
  score: number;
  difficulty: number;
  levelTime?: number; // Time spent on current level
}

export interface MemoryGameState {
  phase: 'memorization' | 'recall' | 'result';
  objects: MemoryObject[];
  playerPlacements: MemoryObject[];
  timeLeft: number;
  recallStartTime?: number; // Time when recall phase started
  levelStartTime?: number; // Time when current level started
  difficulty: number;
  isComplete: boolean;
  stats: GameStats | null;
}

// Advanced DDA Types
export interface DifficultySettings {
  gridSize: number;                 // Grid dimensions (3-7)
  objectCount: number;              // Number of objects to remember (2-15)
  memorizationTime: number;         // Seconds to memorize (5-30)
  showGrid: boolean;                // Show grid lines as hint
  colorCoding: boolean;             // Use colors to help memory
  allowUndo: boolean;               // Can undo placements
  similarObjects: boolean;          // Use similar-looking objects (harder)
}

export interface GameMetrics {
  accuracy: number;                 // Percentage correct
  averagePlacementTime: number;     // Average time per placement
  objectsCorrect: number;           // Number of correct placements
  objectsIncorrect: number;         // Number of wrong placements
  totalAttempts: number;            // Total rounds attempted
  perfectRounds: number;            // Rounds with 100% accuracy
}

export interface DDAState {
  consecutivePerfect: number;       // Consecutive perfect rounds
  consecutiveFailures: number;      // Consecutive rounds with <50% accuracy
  failuresInLastFive: number;       // Count failures in last 5 rounds
  lastRoundAccuracy: number;        // Previous round's accuracy
  roundsSincePromotion: number;     // Prevent too-frequent increases
  totalAdjustments: number;         // Count total DDA adjustments
}

export interface RoundData extends GameMetrics {
  timestamp: string;
  difficulty: DifficultySettings;
  placementTimes: number[];
  gridSize: number;
  objectCount: number;
}

// DDA Configuration
export const DDA_CONFIG = {
  // Accuracy thresholds
  PERFECT_ACCURACY: 1.0,            // 100% triggers promotion consideration
  HIGH_ACCURACY: 0.90,              // 90%+ triggers promotion (harder to advance)
  LOW_ACCURACY: 0.60,               // <60% triggers relief (easier relief)
  TARGET_ACCURACY: 0.80,            // Target sweet spot
  
  // Time thresholds (seconds per object)
  FAST_PLACEMENT: 2,                // Very fast - player confident
  SLOW_PLACEMENT: 8,                // Slow - player struggling
  
  // Streak requirements
  MIN_PERFECT_STREAK: 3,            // 3 perfect rounds before promotion (slower advancement)
  MIN_FAILURE_STREAK: 2,            // Poor rounds before relief
  
  // Constraints
  MIN_GRID_SIZE: 3,
  MAX_GRID_SIZE: 6,                 // Reduced max grid size (easier)
  MIN_OBJECT_COUNT: 2,
  MAX_OBJECT_COUNT: 12,             // Reduced max objects (easier)
  MIN_MEMORIZATION_TIME: 8,         // Longer minimum time (easier)
  MAX_MEMORIZATION_TIME: 35,        // Even more max time (easier)
};

// Initial settings
export const INITIAL_DIFFICULTY: DifficultySettings = {
  gridSize: 3,                      // Smaller grid (easier)
  objectCount: 3,                   // Keep 3 objects
  memorizationTime: 20,             // More time to memorize (easier)
  showGrid: true,                   // Keep grid visible
  colorCoding: true,                // Keep color coding
  allowUndo: true,                  // Allow undo
  similarObjects: false,            // No similar objects to start
};

export const INITIAL_DDA_STATE: DDAState = {
  consecutivePerfect: 0,
  consecutiveFailures: 0,
  failuresInLastFive: 0,
  lastRoundAccuracy: 0,
  roundsSincePromotion: 0,
  totalAdjustments: 0,
};

export const INITIAL_METRICS: GameMetrics = {
  accuracy: 0,
  averagePlacementTime: 0,
  objectsCorrect: 0,
  objectsIncorrect: 0,
  totalAttempts: 0,
  perfectRounds: 0,
};

export const MEMORIZATION_TIME = 20; // seconds (deprecated - use DifficultySettings)
export const INITIAL_OBJECT_COUNT = 3; // Start with fewer objects (deprecated)
export const GRID_SIZE = 5; // 5x5 grid (deprecated)

// Object types with their emojis and colors
export const GAME_OBJECTS = [
  { type: '📚', name: 'book', color: '#FFB5B5' },
  { type: '🔑', name: 'key', color: '#FFE4B5' },
  { type: '🍎', name: 'apple', color: '#B5FFB5' },
  { type: '☕', name: 'coffee', color: '#B5E4FF' },
  { type: '🎮', name: 'gamepad', color: '#E4B5FF' },
  { type: '⌚', name: 'watch', color: '#FFB5E4' },
  { type: '📱', name: 'phone', color: '#B5FFE4' },
  { type: '🎵', name: 'music', color: '#E4FFB5' },
  { type: '💡', name: 'lamp', color: '#FFE4E4' },
  { type: '🎨', name: 'art', color: '#E4E4FF' },
];